import { useState, useContext, useEffect, useRef } from "react";
import Header from "../../Components/Header";
import ProgressBar from "../../Components/ProgressBar";
import { useNavigate } from "react-router-dom";
import "./step2.css";
import PortalContext from "../PortalContext";
import { sendPostRequest } from "../../Client/index.js";
import ClipLoader from "react-spinners/ClipLoader";
import { useGetTranslation } from "../../Components/Translate";

const countyOptionValue = (name, code) => `${name} - ${code}`;

const resolveCountySelectValue = (rawCounty, statesMap) => {
  if (!rawCounty || !statesMap) return "";

  const target = String(rawCounty).trim().toLowerCase();
  const entries = Object.entries(statesMap);

  const byCode = entries.find(
    ([, code]) => String(code).trim().toLowerCase() === target
  );
  if (byCode) return countyOptionValue(byCode[0], byCode[1]);

  const byName = entries.find(
    ([name]) => String(name).trim().toLowerCase() === target
  );
  if (byName) return countyOptionValue(byName[0], byName[1]);

  const byOption = entries.find(
    ([name, code]) => countyOptionValue(name, code).toLowerCase() === target
  );
  if (byOption) return countyOptionValue(byOption[0], byOption[1]);

  return "";
};

export default function Step2() {
  const [countyDropdown, setCountyDropdown] = useState(false);

  const [country, setCountry] = useState("");

  const name = useRef();
  const address1 = useRef();
  const address2 = useRef();
  const town = useRef();
  const county = useRef();
  const postcode = useRef();

  const [fetched, setFetched] = useState(false);
  const [matched, setMatched] = useState(false);

  const { portal } = useContext(PortalContext);

  const [refInitialData, setRefInitialData] = useState({});

  const getTranslation = useGetTranslation();

  const navigate = useNavigate();

  const selectedCounty = resolveCountySelectValue(
    refInitialData?.Customer_county || portal?.step2?.county,
    portal?.data?.states?.[country]
  );

  const portalLookup = async (id) => {
    if (!portal?.step1?.orderNumber) {
      navigate(`/`);
      return;
    }

    const res = await sendPostRequest("api/v2-label-portal-lookup.php", {
      orderNumber: portal?.step1.orderNumber,
      email: portal?.step1.email,
      postcode: portal?.step1.postcode,
    });

    if (
      !res?.SKU?.length &&
      portal?.data?.settings?.integrationMatchRequired === 1
    ) {
      navigate(`/`, {
        state: {
          notice: `Your order details could not be found. Check the details and resubmit, or contact ${portal?.data?.settings?.tradingName} directly on how to proceed with your return.`,
        },
      });
      return;
    }

    portal.SKUs = res.SKU;

    setRefInitialData(res);

    setCountry(
      res?.["Customer_country ISO"] &&
        res["Customer_country ISO"].trim().length === 2
        ? res["Customer_country ISO"].trim().toUpperCase()
        : portal?.step2?.country || ""
    );

    setMatched(res?.match);
    setFetched(true);
  };

  useEffect(() => {
    portalLookup();
  }, []);

  const showCountyDropdown =
    (countyDropdown && country === "AU") ||
    country === "CA" ||
    country === "AT" ||
    country === "IT" ||
    country === "US";

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      showCountyDropdown &&
      (!county.current.value ||
        county.current.value === "Please select a County / State")
    ) {
      alert("Please select County / State");
      return;
    }

    const step2 = {
      name: name.current.value,
      address1: address1.current.value,
      address2: address2.current.value,
      town: town.current.value,
      county: county.current.value,
      postcode: postcode.current.value,
      country: country,
      matched: matched,
    };

    portal.step2 = step2;

    if (portal?.SKUs?.length > 0) {
      // navigate(`/step-3`, {
      //   replace: true,
      // });
      navigate(`/step-3`);
    } else {
      // navigate(`/step-3-manual`, {
      //   replace: true,
      // });
      navigate(`/step-3-manual`);
    }
  };

  useEffect(() => {
    if (!country) return;

    let foundMatch = portal.data.states.hasOwnProperty(country);

    setCountyDropdown(foundMatch);
  }, [country]);

  return (
    <>
      <Header />

      <section class="page3-slider py-lg-3">
        <div class="container">
          <div class="row d-flex align-items-center">
            <div class="col-lg-12 col-12 pb-5">
              <ProgressBar step={2} />

              {!fetched ? (
                <div className="d-flex justify-content-center mt-5">
                  <ClipLoader
                    color={"#d9e5f4"}
                    size={75}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                  />
                </div>
              ) : (
                <form class="align-items-center mt-3" onSubmit={handleSubmit}>
                  <div class="row mt-2 mobile-no-wrap">
                    <div class="col-sm-4">
                      <select
                        id="country"
                        name="country"
                        required
                        value={country || ""}
                        onChange={(e) => setCountry(e.target.value)}
                        className=""
                        style={{
                          appearance: "none",
                          WebkitAppearance: "none",
                          MozAppearance: "none",
                          backgroundColor: "white",
                          color: "black",
                          paddingRight: "2.5rem",
                          backgroundImage:
                            "url(\"data:image/svg+xml;utf8,<svg fill='%23333' height='12' viewBox='0 0 20 20' width='12' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>\")",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "right 0.75rem center",
                          backgroundSize: "1rem",
                          border: "1px solid #ccc",
                          borderRadius: "5px",
                        }}
                      >
                        <option value="" disabled>
                          Select a Country
                        </option>

                        {portal?.data?.settings?.countries?.map((c) => (
                          <option value={c.iso} key={c.iso}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div class="row mt-2 mobile-no-wrap">
                    <div class="col-sm-4">
                      <input
                        required
                        type="text"
                        placeholder={getTranslation(
                          "STEP2_PLACEHOLDER_YOUR_NAME",
                          "Your name"
                        )}
                        name="name"
                        ref={name}
                        defaultValue={
                          refInitialData?.Customer_name
                            ? refInitialData?.Customer_name
                            : portal?.step2?.name
                            ? portal?.step2?.name
                            : ""
                        }
                      ></input>
                    </div>
                    <div class="col-sm-4">
                      <input
                        required
                        type="text"
                        placeholder={getTranslation(
                          "STEP2_PLACEHOLDER_ADDRESS_LINE_1",
                          "Address line 1"
                        )}
                        name="address1"
                        ref={address1}
                        defaultValue={
                          refInitialData?.["Customer_address 1"]
                            ? refInitialData?.["Customer_address 1"]
                            : portal?.step2?.address1
                            ? portal?.step2?.address1
                            : ""
                        }
                      ></input>
                    </div>
                  </div>

                  <div class="row mt-2 mobile-no-wrap">
                    <div class="col-sm-4">
                      <input
                        type="text"
                        placeholder={getTranslation(
                          "STEP2_PLACEHOLDER_ADDRESS_LINE_2",
                          "Address line 2"
                        )}
                        name="address2"
                        ref={address2}
                        defaultValue={
                          refInitialData?.["Customer_address 2"]
                            ? refInitialData?.["Customer_address 2"]
                            : portal?.step2?.address2
                            ? portal?.step2?.address2
                            : ""
                        }
                      ></input>
                    </div>
                    <div class="col-sm-4">
                      <input
                        required
                        type="text"
                        placeholder={getTranslation(
                          "STEP2_PLACEHOLDER_TOWN",
                          "Town"
                        )}
                        name="town"
                        ref={town}
                        defaultValue={
                          refInitialData?.Customer_town
                            ? refInitialData?.Customer_town
                            : portal?.step2?.town
                            ? portal?.step2?.town
                            : ""
                        }
                      ></input>
                    </div>
                  </div>

                  <div class="row mt-2 mobile-no-wrap">
                    {showCountyDropdown ? (
                      <div class="col-sm-4">
                        <select
                          id="county"
                          name="county"
                          key={country}
                          ref={county}
                          defaultValue={selectedCounty}
                          required
                          className=""
                          style={{ backgroundColor: "white", color: "black" }}
                        >
                          <option key={"select"} value="">
                            {getTranslation(
                              "STEP2_PLACEHOLDER_STATE_FREETEXT",
                              "Please select a County / State"
                            )}
                          </option>
                          {Object.entries(
                            portal?.data?.states?.[country] || {}
                          ).map(([item, key]) => (
                            <option
                              key={item}
                              value={countyOptionValue(item, key)}
                            >
                              {item} - {key}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div class="col-sm-4">
                        <input
                          type="text"
                          placeholder={getTranslation(
                            "STEP2_PLACEHOLDER_STATE_SELECT",
                            "County / State"
                          )}
                          name="county"
                          ref={county}
                          defaultValue={
                            refInitialData?.Customer_county
                              ? refInitialData?.Customer_county
                              : portal?.step2?.county
                              ? portal?.step2?.county
                              : ""
                          }
                        ></input>
                      </div>
                    )}

                    <div class="col-sm-4">
                      <input
                        required
                        type="text"
                        placeholder={getTranslation(
                          "STEP2_PLACEHOLDER_POSTCODE",
                          "Postcode / Zip"
                        )}
                        name="postcode"
                        ref={postcode}
                        onInput={(e) => {
                          e.target.value = e.target.value.toUpperCase();
                        }}
                        autoCapitalize="characters"
                        autoCorrect="off"
                        spellCheck={false}
                        defaultValue={
                          refInitialData?.Customer_postcode
                            ? refInitialData?.Customer_postcode
                            : portal?.step1?.postcode
                            ? portal?.step1?.postcode
                            : ""
                        }
                      ></input>
                    </div>
                  </div>

                  <div class="row mt-4">
                    <div class="col-sm-8">
                      <button
                        type="submit"
                        style={{
                          backgroundColor: `${portal.data.settings.colours.primary}`,
                        }}
                      >
                        {getTranslation("CONTINUE_BUTTON", "Continue")}
                      </button>
                    </div>

                    <div class="col-sm-4 d-flex justify-content-end">
                      <img
                        src={require("../../Assets/Images/powered-by.png")}
                        class="img-fluid powered-logo"
                      ></img>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
